import { lazy, Suspense, useEffect } from 'react'
import { Spin } from 'antd'
import { Navigate, Route, Routes } from 'react-router-dom'

import { DemoGuard } from './demo/DemoGuard'
import { DEMO_PROJECT_ID } from './demo/demo'
import { useAuth } from './auth/AuthContext'
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary'
import { CookieConsent } from './analytics/CookieConsent'
import { clearChunkReloadGuard } from './components/chunkReloadGuard'
import { LandingPage } from './pages/LandingPage'

/**
 * Everything behind a login, and everything that draws a chart, is fetched when
 * it is first opened.
 *
 * This is a product about performance, so the landing page shipping a flame
 * graph library to somebody who has not signed up is more than an
 * inconsistency. echarts alone is the largest thing in the bundle and it is
 * needed by exactly one part of the application.
 */
const AppLayout = lazy(() => import('./components/AppLayout').then((m) => ({ default: m.AppLayout })))
const AuthPage = lazy(() => import('./pages/AuthPage').then((m) => ({ default: m.AuthPage })))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })))
const PricingPage = lazy(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })))
const RefundPage = lazy(() => import('./pages/RefundPage').then((m) => ({ default: m.RefundPage })))
const TermsPage = lazy(() => import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })))
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })))
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })))
const ProfilesPage = lazy(() => import('./pages/ProfilesPage').then((m) => ({ default: m.ProfilesPage })))
const ServicePage = lazy(() => import('./pages/ServicePage').then((m) => ({ default: m.ServicePage })))
const ComparePage = lazy(() => import('./pages/ComparePage').then((m) => ({ default: m.ComparePage })))
const ProjectPage = lazy(() => import('./pages/ProjectPage').then((m) => ({ default: m.ProjectPage })))
const ProjectSettingsPage = lazy(() => import('./pages/ProjectSettingsPage').then((m) => ({ default: m.ProjectSettingsPage })))
const SharedProfilePage = lazy(() => import('./pages/SharedProfilePage').then((m) => ({ default: m.SharedProfilePage })))
const UploadPage = lazy(() => import('./pages/UploadPage').then((m) => ({ default: m.UploadPage })))
const ConnectPage = lazy(() => import('./pages/ConnectPage').then((m) => ({ default: m.ConnectPage })))
const BillingPage = lazy(() => import('./pages/BillingPage').then((m) => ({ default: m.BillingPage })))
const MembersPage = lazy(() => import('./pages/MembersPage').then((m) => ({ default: m.MembersPage })))
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })))
const InvitePage = lazy(() => import('./pages/InvitePage').then((m) => ({ default: m.InvitePage })))


export function App() {
  const { user, loading } = useAuth()

  // Reaching a render at all means the chunks this page needs were served, so
  // the next deploy is allowed its own single reload.
  useEffect(clearChunkReloadGuard, [])

  // Until the stored token has been checked, rendering either the app or the
  // login page would be a guess, and a wrong guess flashes the wrong screen.
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!user) {
    return (
      <ChunkErrorBoundary>
      <CookieConsent />
      <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* The root is the marketing page: sending an anonymous visitor
            straight to a login form asks them to commit before they know to
            what. */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<AuthPage />} />
        {/* An invitation is opened by somebody with no account yet, so it
            lives in the signed-out tree. */}
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Pricing sits outside the authenticated tree for the same reason the
            landing page does: the person reading it has not signed up yet. */}
        <Route path="/pricing" element={<PricingPage />} />

        {/* Legal pages. Public and in both trees: a payment provider's
            reviewer reads them signed out, and a customer looking up the
            refund policy is usually signed in. */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/refunds" element={<RefundPage />} />

        {/* A public profile link. Outside both trees: it is reachable
            signed in or out, and it must render the same either way — the
            person you sent it to may well be a colleague with an account. */}
        <Route path="/s/:token" element={<SharedProfilePage />} />

        {/* The demo runs on the product's own routes, with sample data and no
            server behind it. Mounting it on `:projectId` rather than a static
            path is what gives every page below a real project parameter. */}
          {/* A public profile link. Outside both trees: it is reachable
            signed in or out, and it must render the same either way — the
            person you sent it to may well be a colleague with an account. */}
        <Route path="/s/:token" element={<SharedProfilePage />} />

      <Route path="/demo" element={<Navigate to={`/projects/${DEMO_PROJECT_ID}`} replace />} />
        <Route path="/projects/:projectId" element={<DemoGuard />}>
          <Route index element={<ProjectPage />} />
          <Route path="services/:serviceId" element={<ServicePage />} />
          <Route path="profiles" element={<ProfilesPage />} />
          <Route path="profiles/:profileId" element={<ProfilePage />} />
          <Route path="compare" element={<ComparePage />} />
          <Route path="connect" element={<ConnectPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ChunkErrorBoundary>
    )
  }

  return (
    <ChunkErrorBoundary>
      <CookieConsent />
      <Suspense fallback={<PageLoading />}>
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/audit" element={<AuditPage />} />
        {/* Where Paddle returns somebody after a payment, so it has to exist
            before the first one is taken. */}
        <Route path="/billing" element={<BillingPage />} />
        {/* Also reachable signed in, where it is how somebody on a trial finds
            out what happens when it ends. */}
        <Route path="/pricing" element={<PricingPage />} />
      </Route>

        {/* Legal pages. Public and in both trees: a payment provider's
            reviewer reads them signed out, and a customer looking up the
            refund policy is usually signed in. */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/refunds" element={<RefundPage />} />


        {/* A public profile link. Outside both trees: it is reachable
            signed in or out, and it must render the same either way — the
            person you sent it to may well be a colleague with an account. */}
        <Route path="/s/:token" element={<SharedProfilePage />} />

      <Route path="/demo" element={<Navigate to={`/projects/${DEMO_PROJECT_ID}`} replace />} />

      <Route path="/projects/:projectId" element={<AppLayout />}>
        <Route index element={<ProjectPage />} />
        <Route path="services/:serviceId" element={<ServicePage />} />
        <Route path="profiles" element={<ProfilesPage />} />
        <Route path="profiles/:profileId" element={<ProfilePage />} />
        <Route path="compare" element={<ComparePage />} />
        <Route path="connect" element={<ConnectPage />} />
        <Route path="upload" element={<UploadPage />} />
        <Route path="settings" element={<ProjectSettingsPage />} />
      </Route>

      <Route path="/" element={<Navigate to="/projects" replace />} />
      <Route path="/login" element={<Navigate to="/projects" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/projects" replace />} />
      <Route path="*" element={<Navigate to="/projects" replace />} />
    </Routes>
    </Suspense>
      </ChunkErrorBoundary>
  )
}

/**
 * What fills the page while a lazily loaded route arrives.
 *
 * Deliberately plain and centred: a spinner that jumps the layout is worse than
 * the fraction of a second it covers, and this is a product whose homepage
 * argues that performance is noticed.
 */
function PageLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 24px' }}>
      <Spin />
    </div>
  )
}
